import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import MinimalLayout from "../../layouts/minimal";
import Alert from "../../components/alert";
import {Form, Formik} from "formik";
import HelperService from "../../services/helper-service";
import Section from "../../components/section";
import {completeProfileSchema, userLocationSchema} from "../../utils/schemas";
import {useGlobalNotificationMessage} from "../../context/message";
import {AppRoutes} from "../../utils/constants";
import {editUser, useUserService} from "../../services/user-service";
import {useCategoryService} from "../../services/category-service";
import {getUserData} from "../../modules/auth-persistor";
import Loading from "../../components/loading";
import FormGroup from "../../components/form-group";
import Select from "react-select";
import MapPicker from "../../components/map-picker";
import {useAuth} from "../../context/auth";

export default function CompleteProfile () {
    const [responseMessage, setResponseMessage] = useState({type: 'danger', message: null});
    const [submitted, setSubmitted] = useState();
    const [model, setModel] = useState(null);
    const [categoriesList, setCategoriesList] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState([]);
    const {addErrorMessage, removeMessage } = useGlobalNotificationMessage();

    const auth = useAuth();

    const formSchema = completeProfileSchema;
    let userService = useUserService();
    let categoryService = useCategoryService();

    useEffect(()=>{

        setSubmitted(auth.currentUser.activated);

        let promises = [
            userService.getUser(getUserData()?.id),
            categoryService.getList(),
        ];

        Promise.all(promises).then(([user=null, categories=[]])=>{
            if (!user){
                user = {};
            }
            if (!user.location){
                user.location = userLocationSchema.cast();
            }
            setModel(formSchema.cast(user));
            let catGroups = categories?.groups.map(
                (group)=>{
                    group.label = group.name ? group.name : 'Other';
                    group.options = [...group.categories.map((item)=>{
                        item.label = item.name;
                        item.value = item.id;
                        return item;
                    })];
                    return group;
                }
            )
            setCategoriesList([...catGroups]);

            setSelectedCategory(user.categories);
        }).catch(error => {
            HelperService.handleApiErrors(error, addErrorMessage);
        });
    }, []);

    const selectCategory = (items=null)=>{
        setSelectedCategory([...items]);
    };

    function locationChanged(setFieldValue, params){
        setFieldValue('location.lat', params?.lat || '');
        setFieldValue('location.lon', params?.lon || '');
        setFieldValue('location.address', params?.address || '');
        setFieldValue('location.city', params?.city || '');
    }

    const reloadUser = async ()=>{
        return new Promise((resolve, reject)=>{
            userService.getUser(getUserData()?.id)
                .then((user)=>{
                    setModel(user);
                    resolve(user);
                })
                .catch(error => {
                    HelperService.handleApiErrors(error, addErrorMessage);
                    reject(error);
                });
        })
    }

    const handleSubmit = (values, { setSubmitting, setErrors }) => {
        removeMessage();
        let requestParams = Object.assign({}, values);
        requestParams.categories = selectedCategory.map(c=>c.id);
        requestParams.location = values.location?.lat ? values.location : null;

        delete requestParams.trainer;
        delete requestParams.paymentInfo;
        delete requestParams.photo;

        editUser(auth.currentUser.id, requestParams)
            .then(async (responseData) => {
                //reload user with new data and propagate to saved profile info trough app.
                const u = await reloadUser();
                auth.doAppUserLogin(u);
                setSubmitted(true);
            })
            .catch(error => {
                HelperService.handleApiErrors(error, addErrorMessage);
                HelperService.handleFormErrors(error, setErrors);
            })
            .finally(()=>{setSubmitting(false);});
    };

    return (
        <>
            <Section animation={HelperService.getRandomAnimation()} className="bg-image">

                <div className="container">
                    <div className="row justify-content-center align-items-center h100 py-5">
                        <div className="col-xxl-8 col-lg-8">

                            <div className="text-center pb-5">
                                <Link to={AppRoutes.landing.path}>
                                    <span><img src="/assets/images/logo.svg" alt="" height="120"/></span>
                                </Link>
                            </div>

                            <div className="card">

                                {!submitted && <div className="card-body p-4">
                                    <div className="text-center w-75 m-auto">
                                        <h4 className="text-dark-50 text-center pb-0 fw-bold">Complete profile</h4>
                                        <p className="text-muted mb-4">We just need a few more things about you</p>
                                    </div>
                                    <Alert type={responseMessage.type} className={"my-2"}>{responseMessage.message}</Alert>
                                    {!model && <Loading className="m-2" />}

                                    {model && <Formik
                                        enableReinitialize={true}
                                        initialValues={formSchema.cast(model)}
                                        validationSchema={formSchema}
                                        onSubmit={handleSubmit}
                                    >
                                        {({isSubmitting, errors, touched, values, isValid, isValidating, setFieldValue}) => (
                                            <Form className="row">

                                                <hr/>

                                                <FormGroup
                                                    name="name"
                                                    placeholder="First name"
                                                    label="Name"
                                                    className="mb-3 col-md-4"
                                                    touched={touched} errors={errors} schema={formSchema}
                                                />

                                                <FormGroup
                                                    name="surname"
                                                    placeholder="Last name"
                                                    label="Last name"
                                                    className="mb-3 col-md-4"
                                                    touched={touched} errors={errors} schema={formSchema}
                                                />

                                                <FormGroup
                                                    name="mobile"
                                                    placeholder="Number with area code"
                                                    label="Mobile"
                                                    className="mb-3 col-md-4"
                                                    touched={touched} errors={errors} schema={formSchema}
                                                />

                                                <hr/>

                                                <div className="form-group pb-3">
                                                    <label htmlFor="categories" className="form-label">What fitness activities are you
                                                        interested in?</label>
                                                    <Select
                                                        options={categoriesList}
                                                        placeholder={'Choose category'}
                                                        closeMenuOnSelect={false}
                                                        isSearchable
                                                        isClearable
                                                        isMulti
                                                        value={selectedCategory?.map(
                                                            (item) => {
                                                                item.value = item.id;
                                                                item.label = item.name;
                                                                return item;
                                                            }
                                                        )}
                                                        onChange={selectCategory}
                                                    />
                                                </div>

                                                <hr/>

                                                <div className="form-group pb-3">
                                                    <label htmlFor="location" className="form-label">Choose your location</label>
                                                    <div>
                                                        <MapPicker
                                                            onChange={(params)=>locationChanged(setFieldValue, params)}
                                                            lat={model?.location?.lat || null}
                                                            lon={model?.location?.lon || null}
                                                        />
                                                    </div>
                                                </div>

                                                <FormGroup
                                                    name="location.address"
                                                    placeholder="Address"
                                                    label="Address"
                                                    className="mb-3 col-md-6"
                                                    touched={touched} errors={errors} schema={formSchema}
                                                />

                                                <FormGroup
                                                    name="location.city"
                                                    placeholder="City"
                                                    label="City"
                                                    className="mb-3 col-md-6"
                                                    touched={touched} errors={errors} schema={formSchema}
                                                />

                                                <hr />

                                                <FormGroup
                                                    name="emailNotifications"
                                                    type="checkbox"
                                                    placeholder="enable/disable"
                                                    label="Notifications"
                                                    checkLabel="Enable email notifications"
                                                    className="mb-3 col-md-4"
                                                    touched={touched} errors={errors} schema={formSchema}
                                                />

                                                {!isValid && !isValidating && isSubmitting && errors && <div className="text-center my-2">
                                                    <p>The information is incomplete; please review all fields and re-submit. </p>
                                                    <div className="p-2 border-danger border-2 rounded-2" dangerouslySetInnerHTML={{__html:HelperService.objectPrettyPrint(errors, 2)}}></div>
                                                </div>}

                                                <div className={"text-center mt-2"}>
                                                    <button type="submit" className="btn btn-primary w-100"
                                                            disabled={isSubmitting}>Confirm
                                                    </button>
                                                </div>
                                            </Form>
                                        )}
                                    </Formik>}
                                </div>}

                                {submitted && <div className="card-body p-4">
                                    <div className={"my-2 p-2 text-center"}>
                            <span className="mdi mdi-check-circle-outline mb-2 text-success"
                                  style={{'fontSize': '50px'}}></span><br/>
                                        <h4 className="text-dark-50 text-center pb-0 fw-bold">Profile completed</h4>
                                        <p className="text-muted mb-4">Thank you for taking the time to complete your profile! We will try to find you events that interest you!</p>
                                        <Link to={AppRoutes.home.path}><button className='btn btn-primary'>Discover</button></Link>
                                    </div>
                                </div>}

                            </div>

                        </div>
                    </div>
                </div>

            </Section>
        </>
    );
}


CompleteProfile.getLayout = function getLayout(page) {
    return (
        <MinimalLayout>
            {page}
        </MinimalLayout>
    )
}
